import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import workOrdersApi from "../../api/workOrdersApi";
import { useToastStack, ToastStack } from "../../../../shared/components/ActionToast";

// WO-CLOSING-PHOTOS-0414: same allowlist as CompletionDialog — keeps the
// OS file picker filter consistent across both rework + closing flows.
const ACCEPTED_FILE_TYPES =
  "image/jpeg,image/png,image/gif,image/webp," +
  "application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "text/plain,text/csv,text/markdown,text/rtf,application/rtf," +
  ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.md,.log,.rtf";

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

interface ReworkDialogProps {
  open: boolean;
  workOrderId: string;
  workOrderNumber: string;
  onConfirm: (notes: string) => void;
  onCancel: () => void;
}

/**
 * Modal dialog for managers returning a WO for rework (from PendingReview back to InProgress).
 * Collects rework instructions (required) and optional file attachments.
 * Matches the Paper design (artboard A3X-0) and follows CompletionDialog overlay/animation pattern.
 */
const ReworkDialog = ({
  open,
  workOrderId,
  workOrderNumber,
  onConfirm,
  onCancel,
}: ReworkDialogProps) => {
  const { translate } = useTranslation("operations");
  const [reworkNotes, setReworkNotes] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast, dismissToast, toasts } = useToastStack();

  // Reset on open
  useEffect(() => {
    if (open) {
      setReworkNotes("");
      setUploadedFiles([]);
      setUploading(false);
      setDragOver(false);
    }
  }, [open]);

  // ─── File upload handler ──────────────────────────────────
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (!workOrderId || files.length === 0) return;
    setUploading(true);
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      try {
        const result = await workOrdersApi.uploadFile(workOrderId, file);
        setUploadedFiles(prev => [...prev, {
          id: result.id || result.fileId || crypto.randomUUID(),
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
        }]);
      } catch (err: any) {
        // WO-CLOSING-PHOTOS-0414: surface upload failures so they don't
        // fail silently. Server message is preferred when present.
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.title;
        const message = serverMessage
          ? `${file.name}: ${serverMessage}`
          : translate("panel.uploadFailed", { fileName: file.name });
        addToast("error", message);
      }
    }
    setUploading(false);
  }, [workOrderId, addToast, translate]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  // Inject keyframes once
  useEffect(() => {
    const id = "rework-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes reworkBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes reworkSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm(reworkNotes);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onCancel}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "reworkBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog card */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "reworkSlideUp 0.3s ease",
        }}
      >
        {/* Amber accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #E8A817 0%, #D4972B 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "28px 28px 24px" }}>
          {/* Header row: title + close button */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0C1117",
                  mb: "4px",
                }}
              >
                {translate("board.returnForRework")}
              </Box>
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#3B82F6",
                }}
              >
                {workOrderNumber}
              </Box>
            </Box>
            <Box
              onClick={onCancel}
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "1px solid #E8E5DF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
                mt: "2px",
                "&:hover": {
                  borderColor: "#D1CCC4",
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "16px", color: "#8C9099" }} />
            </Box>
          </Box>

          {/* Rework Instructions — required */}
          <Box sx={{ mt: "24px", mb: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {translate("board.reworkInstructions")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#E8A817",
              }}
            >
              {translate("fields.required")}
            </Box>
          </Box>
          <textarea
            value={reworkNotes}
            onChange={(e) => setReworkNotes(e.target.value)}
            placeholder={translate("board.reworkPlaceholder")}
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontFamily: "'Sora', sans-serif",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#0C1117",
              backgroundColor: "#F7F5F0",
              border: "1.5px solid transparent",
              borderRadius: "12px",
              padding: "14px 16px",
              outline: "none",
              resize: "vertical",
              minHeight: "100px",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E8A817";
              e.target.style.boxShadow = "0 0 0 3px rgba(232,168,23,0.08)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "transparent";
              e.target.style.boxShadow = "none";
            }}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: "#8C9099",
              mt: "6px",
            }}
          >
            {translate("board.reworkHelper")}
          </Box>

          {/* Attachments — optional */}
          <Box sx={{ mt: "20px", mb: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {translate("createPanel.attachments")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("fields.optional")}
            </Box>
          </Box>
          {/* Drop zone */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              backgroundColor: dragOver ? "rgba(232,168,23,0.04)" : "#F7F5F0",
              border: dragOver ? "1.5px dashed #E8A817" : "1.5px dashed #D1CCC4",
              borderRadius: "12px",
              padding: "24px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#E8A817",
                backgroundColor: "rgba(232,168,23,0.02)",
              },
            }}
          >
            {/* Plus icon */}
            <Box
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="#8C9099" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#4A4F57",
              }}
            >
              {translate("panel.dragFilesHere")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#8C9099",
              }}
            >
              {translate("panel.fileFormats")}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.length) {
                  handleFiles(e.target.files);
                  e.target.value = "";
                }
              }}
            />
          </Box>
          {/* Uploaded file list */}
          {(uploadedFiles.length > 0 || uploading) && (
            <Box sx={{ mt: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {uploadedFiles.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#F7F5F0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                  }}
                >
                  {/* File icon */}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z" stroke="#8C9099" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 1v4h4" stroke="#8C9099" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#4A4F57",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {f.fileName}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      color: "#8C9099",
                      flexShrink: 0,
                    }}
                  >
                    {f.fileSize < 1024 * 1024
                      ? `${(f.fileSize / 1024).toFixed(0)} KB`
                      : `${(f.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                  </Box>
                </Box>
              ))}
              {uploading && (
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099",
                    padding: "4px 12px",
                  }}
                >
                  {translate("panel.uploading")}
                </Box>
              )}
            </Box>
          )}

          {/* Action bar */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px", mt: "24px" }}>
            <Box
              onClick={onCancel}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                "&:hover": { color: "#4A4F57" },
              }}
            >
              {translate("actions.cancel")}
            </Box>
            <Box
              component="button"
              onClick={handleConfirm}
              disabled={!reworkNotes.trim()}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                backgroundColor: "#E8A817",
                border: "none",
                borderRadius: "24px",
                padding: "10px 28px",
                cursor: reworkNotes.trim() ? "pointer" : "default",
                outline: "none",
                opacity: reworkNotes.trim() ? 1 : 0.5,
                transition: "all 0.2s ease",
                "&:hover": reworkNotes.trim()
                  ? {
                      backgroundColor: "#D4972B",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 16px rgba(232,168,23,0.3)",
                    }
                  : {},
              }}
            >
              {translate("board.returnToWorker")}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* WO-CLOSING-PHOTOS-0414: surface upload failures (file type,
          size, etc.) so they don't fail silently. */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </Box>
  );
};

export default ReworkDialog;
